import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Star, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ value, onChange, readonly = false, size = 20 }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          onClick={() => !readonly && onChange(i)}
          onMouseEnter={() => !readonly && setHover(i)}
          onMouseLeave={() => setHover(0)}
          className={`transition-transform ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          disabled={readonly}
          data-testid={`star-${i}`}
        >
          <Star
            size={size}
            strokeWidth={2}
            fill={(hover || value) >= i ? '#FFDE00' : 'none'}
            className={(hover || value) >= i ? 'text-[#FFDE00]' : 'text-gray-300'}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewSection({ showId }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState({ average: 0, count: 0 });
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/shows/${showId}/reviews`).then(r => setReviews(r.data)).catch(() => {});
    axios.get(`${API}/shows/${showId}/rating`).then(r => setRating(r.data)).catch(() => {});
  }, [showId]);

  useEffect(() => {
    if (user && reviews.length > 0) {
      const mine = reviews.find(r => r.user_id === user.id || r.user_name === user.name);
      if (mine) { setMyRating(mine.rating); setMyComment(mine.comment || ''); }
    }
  }, [user, reviews]);

  const submitReview = async () => {
    if (!user) { toast.error('Sign in to leave a review'); return; }
    if (myRating === 0) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { show_id: showId, rating: myRating, comment: myComment }, { withCredentials: true });
      toast.success('Review submitted!');
      const [revRes, ratRes] = await Promise.all([
        axios.get(`${API}/shows/${showId}/reviews`),
        axios.get(`${API}/shows/${showId}/rating`),
      ]);
      setReviews(revRes.data);
      setRating(ratRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, { withCredentials: true });
      toast.success('Review deleted');
      setReviews(r => r.filter(rv => rv.id !== id));
      setMyRating(0); setMyComment('');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="mt-10" data-testid="review-section">
      {/* Rating Summary */}
      <div className="flex items-center gap-4 mb-6">
        <h2 className="font-['Fredoka'] text-2xl font-bold text-[#0A0A0A]">Reviews</h2>
        {rating.count > 0 && (
          <div className="flex items-center gap-2">
            <StarRating value={Math.round(rating.average)} readonly size={18} />
            <span className="font-['Fredoka'] font-bold text-lg text-[#0A0A0A]">{rating.average}</span>
            <span className="text-sm text-[#4B5563] font-['Nunito']">({rating.count} review{rating.count !== 1 ? 's' : ''})</span>
          </div>
        )}
      </div>

      {/* Write Review */}
      {user && (
        <div className="neo-card p-5 mb-6 bg-[#FDFBEC]" data-testid="write-review">
          <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A] mb-3">Your Rating</h3>
          <StarRating value={myRating} onChange={setMyRating} size={28} />
          <textarea
            value={myComment}
            onChange={(e) => setMyComment(e.target.value)}
            placeholder="Share your thoughts about this show... (optional)"
            rows={3}
            className="w-full mt-3 px-3 py-2 border-2 border-[#0A0A0A] rounded-lg text-sm font-['Nunito'] resize-none focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
            data-testid="review-comment-input"
          />
          <button
            onClick={submitReview}
            disabled={submitting || myRating === 0}
            className="neo-btn mt-3 flex items-center gap-1 px-4 py-2 rounded-lg bg-[#FF3860] text-white text-sm disabled:opacity-50"
            data-testid="submit-review-btn"
          >
            <Send size={14} strokeWidth={2.5} /> {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      )}

      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div className="space-y-3">
          {reviews.map(r => (
            <div key={r.id} className="neo-card p-4 bg-white" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#4AC6FF] flex items-center justify-center text-white font-['Fredoka'] font-bold text-sm border-2 border-[#0A0A0A]">
                    {(r.user_name || 'A').charAt(0).toUpperCase()}
                  </div>
                  <span className="font-['Fredoka'] font-bold text-sm text-[#0A0A0A]">{r.user_name || 'Anonymous'}</span>
                  <StarRating value={r.rating} readonly size={14} />
                </div>
                {user && (r.user_id === user.id || user.role === 'admin') && (
                  <button onClick={() => deleteReview(r.id)} className="text-[#FF007F] hover:text-[#FF3860]" data-testid={`delete-review-${r.id}`}>
                    <Trash2 size={14} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              {r.comment && <p className="text-sm text-[#4B5563] font-['Nunito']">{r.comment}</p>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center py-6 text-[#4B5563] font-['Nunito']">No reviews yet. Be the first to share your thoughts!</p>
      )}
    </div>
  );
}
